import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
} from 'react';
import { CSSObject } from 'styled-components';
import { isInDesktopMode } from 'libs/gutClient/electronBridge';
import Portal from '../Portal';
import Overlay from '../Overlay';

import { DialogStyled } from './dialog.styled';
import { getScrollbarWidth } from '../../utils';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  // If anchorEl isn't provided, set the position with sx prop
  anchorEl?: HTMLElement;
  offsetTop?: number;
  offsetBottom?: number;
  fullHeight?: boolean;
  fullWidth?: boolean;
  overlayStyle?: CSSObject;
  sx?: CSSObject;
  placement?: 'bottom-end' | 'right-top' | 'left-top';
  children?: React.ReactNode | React.ReactNode[];
}

const Dialog = ({
  open,
  onClose,
  overlayStyle,
  offsetBottom,
  fullHeight,
  fullWidth,
  placement,
  anchorEl,
  children,
  ...rest
}: DialogProps) => {
  const [position, setPosition] = useState({});
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  const desktopMode = isInDesktopMode();

  const setMenuCoordinates = () => {
    if (anchorEl && dialogRef.current !== null) {
      const {
        top: anchorTop,
        left: anchorLeft,
        width: anchorWidth,
        height: anchorHeight,
      } = anchorEl.getBoundingClientRect();

      const { width: dialogWidth } = dialogRef.current.getBoundingClientRect();
      const modalStyle = getComputedStyle(dialogRef.current);
      // set position on top of anchor element
      const topPosition = `calc(${anchorTop + anchorHeight}px - ${
        modalStyle.borderWidth
      } + ${window.scrollY}px)`;

      if (placement === 'bottom-end') {
        setPosition({
          left: `calc(${anchorLeft - dialogWidth + anchorWidth}px + ${
            modalStyle.borderWidth
          })`,
          top: topPosition,
        });
      }

      if (placement === 'right-top') {
        setPosition({
          left: anchorLeft + anchorWidth,
          top: offsetBottom,
        });
      } else if (placement === 'left-top') {
        setPosition({
          right: modalStyle.borderWidth,
          top: topPosition,
        });
      }
    }
  };

  useLayoutEffect(() => setMenuCoordinates(), [anchorEl]);

  const handleKeyDown = useCallback(
    event => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [open],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const lastMouseDownTarget = useRef(null);

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const { target } = event;

    if (
      target === overlayRef.current &&
      lastMouseDownTarget.current === overlayRef.current &&
      open
    ) {
      onClose();
    }
  };

  const handleOverlayMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    const { target } = event;
    lastMouseDownTarget.current = target;
  };

  useEffect(() => {
    const resetStyles = () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };

    if (open) {
      const scrollBarWidth = getScrollbarWidth();
      document.body.style.overflow = 'hidden';

      if (scrollBarWidth) {
        document.body.style.paddingRight = `${scrollBarWidth}px`;
      }
    } else {
      resetStyles();
    }

    return () => {
      resetStyles();
    };
  }, [open]);

  if (open) {
    return (
      <Portal>
        <Overlay
          // onClick={handleOverlayClick}
          onMouseDown={handleOverlayMouseDown}
          onMouseUp={handleOverlayClick}
          sx={overlayStyle}
          ref={overlayRef}
        >
          <DialogStyled
            desktopMode={desktopMode}
            fullHeight={fullHeight}
            fullWidth={fullWidth}
            ref={dialogRef}
            style={position}
            {...rest}
          >
            {children}
          </DialogStyled>
        </Overlay>
      </Portal>
    );
  }

  return null;
};

Dialog.defaultProps = {
  placement: 'bottom-end',
  onClose: () => {},
  offsetBottom: 0,
  open: false,
};

export default Dialog;
